import React from 'react';
import { HubMarketingNav } from '../../components/ui/HubHeader';
import Seo from '../../components/Seo';

const SheetsPage = () => {
  const configuredMarketplaceUrl = (import.meta.env.VITE_SHEETS_MARKETPLACE_URL || '').trim();
  const marketplaceUrl = configuredMarketplaceUrl || 'https://workspace.google.com/marketplace/search/Neevii%20Smart%20Expenses';
  const sheetsDemoVideoDesktop = '/assets/videos/Neevii_sheet_Demo3.mp4';
  const sheetsDemoVideoMobile = '/assets/videos/Neevii_sheet_Demo3_mobile.mp4';
  const sheetsDemoVideoPoster = '/assets/videos/Neevii_sheet_Demo3_poster.jpg';
  return (
    <>
      <Seo
        title="AI Receipt Scanner & Categorizer for Google Sheets | Neevii"
        description="Stop manual data entry. Use Neevii AI to scan receipts on your phone and sync them directly to Google Sheets with tax-compliant categories."
      />
      <div className="min-h-screen text-foreground">
        <HubMarketingNav preset="sheets" />
        <main className="pt-10 pb-16 px-6 md:pt-12 lg:px-8">
          <div className="max-w-[92rem] mx-auto space-y-10">
            <section className="rounded-3xl border border-border bg-card/40 p-8 lg:p-12">
              <div className="space-y-6">
                <span className="inline-flex rounded-full bg-[#065aaf] px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white">
                  Neevii Smart Expenses for Google Sheets
                </span>
                <div className="max-w-3xl space-y-6">
                  <h1 className="hub-font-title text-[clamp(1.1rem,4.75vw,5.25rem)] font-light leading-tight tracking-[-0.0125em]">
                    Neevii Smart Expenses
                  </h1>
                  <p className="text-lg text-muted-foreground">
                    Add Neevii Smart Expenses for Google Sheets and map receipts
                    into tax-ready categories quickly.
                  </p>
                </div>
                <p className="inline-flex rounded-lg bg-[#065aaf] px-3 py-1.5 text-sm font-semibold text-white">
                  Start for free
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <article className="overflow-hidden rounded-2xl border border-border bg-card/40">
                    <img
                      src="/assets/images/neevii-sheets-main-v1.png"
                      alt="Neevii Sheets main workspace preview"
                      className="h-72 sm:h-80 lg:h-[25rem] w-full object-contain bg-card/40"
                    />
                    <p className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Sheets layout preview
                    </p>
                  </article>
                  <article className="overflow-hidden rounded-2xl border border-border bg-card/40">
                    <img
                      src="/assets/images/neevii-sheets-sidebar-thumb-v1.png"
                      alt="Neevii Sheets sidebar preview"
                      className="h-72 sm:h-80 lg:h-[25rem] w-full object-contain bg-card/40"
                    />
                    <p className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Sidebar preview
                    </p>
                  </article>
                </div>
                <div className="flex flex-wrap gap-3">
                  <a
                    href={marketplaceUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center rounded-lg bg-brand px-6 py-3 text-sm font-semibold uppercase tracking-wide text-slate-900 hover:bg-brand/90"
                  >
                    Get the Sidebar
                  </a>
                  <a
                    href="/sheets-setup-guide"
                    className="inline-flex items-center justify-center rounded-lg border border-[#065aaf] px-6 py-3 text-sm font-semibold uppercase tracking-wide text-[#065aaf] hover:bg-[#065aaf]/10"
                  >
                    Open Setup Guide
                  </a>
                </div>
              </div>
            </section>

            <section
              id="open-after-install"
              className="rounded-2xl border border-[#065aaf]/30 bg-[#065aaf]/5 p-6 lg:p-8 space-y-4"
            >
              <h2 className="text-2xl font-semibold">Open the add-on after install</h2>
              <p className="text-muted-foreground max-w-3xl">
                After you install from Google Workspace Marketplace, open Neevii from the
                Google Sheets <strong>Extensions</strong> menu — not a separate top-level
                menu such as &ldquo;Neevii AI&rdquo; (that path is from an older bound-script
                setup and is not the Marketplace install).
              </p>
              <ol className="list-decimal pl-6 space-y-2 text-muted-foreground max-w-3xl">
                <li>Open or create a Google Sheet at sheets.google.com.</li>
                <li>
                  Click <strong>Extensions</strong>, then <strong>Neevii Smart Expenses</strong>.
                </li>
                <li>
                  Click <strong>Open/Reopen Neevii Sidebar</strong> (or use the add-on card if
                  prompted).
                </li>
                <li>
                  In the sidebar, click <strong>Continue with Google</strong>, then{' '}
                  <strong>Create expense template</strong>.
                </li>
              </ol>
              <p className="text-sm text-muted-foreground">
                You can also open the right-hand add-ons panel in Sheets and select Neevii Smart
                Expenses if your workspace shows that surface.
              </p>
            </section>

            <section className="grid gap-6 md:grid-cols-2">
              <article className="rounded-2xl border border-border bg-card/30 p-6">
                <h2 className="text-2xl font-semibold mb-3">UK Sole Trader</h2>
                <p className="text-muted-foreground">
                  Categorize spend into HMRC-friendly buckets so your Google
                  Sheets ledger is ready for Self Assessment and bookkeeping
                  handoff.
                </p>
              </article>
              <article className="rounded-2xl border border-border bg-card/30 p-6">
                <h2 className="text-2xl font-semibold mb-3">US Freelancer</h2>
                <p className="text-muted-foreground">
                  Map extracted line items to IRS Schedule C style categories so
                  your sheet is consistent for tax prep and accountant review.
                </p>
              </article>
            </section>

            <section
              id="technical-integrity"
              className="rounded-2xl border border-border bg-card/30 p-6 lg:p-8"
            >
              <h2 className="text-3xl font-semibold mb-4">Built for trust</h2>
              <ul className="list-disc pl-6 space-y-3 text-muted-foreground">
                <li>
                  We request only the Google permissions needed for Sheets
                  functionality.
                </li>
                <li>
                  You see clear Google consent screens before any access is
                  granted.
                </li>
                <li>
                  Sidebar data processing is documented in our Privacy Policy,
                  and dedicated Sheets support is available if needed.
                </li>
              </ul>
              <div className="mt-6 rounded-xl border border-brand/30 bg-brand/10 p-4 space-y-3">
                <div className="overflow-hidden rounded-xl border border-border/60 bg-card/40">
                  <video
                    className="w-full h-auto video-controls-on-hover"
                    controls
                    preload="metadata"
                    poster={sheetsDemoVideoPoster}
                    playsInline
                  >
                    <source src={sheetsDemoVideoMobile} type="video/mp4" media="(max-width: 768px)" />
                    <source src={sheetsDemoVideoDesktop} type="video/mp4" />
                  </video>
                </div>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href="/sheets-google-review" className="text-sm font-semibold text-[#065aaf] hover:underline">
                  Google review demo page
                </a>
                <a href="/privacy" className="text-sm font-semibold text-[#065aaf] hover:underline">
                  Privacy Policy
                </a>
                <a href="/terms" className="text-sm font-semibold text-[#065aaf] hover:underline">
                  Terms of Service
                </a>
                <a href="/support-sheets" className="text-sm font-semibold text-[#065aaf] hover:underline">
                  Sidebar Support
                </a>
              </div>
            </section>

            <section className="rounded-2xl border border-border bg-card/20 p-6 lg:p-8">
              <h2 className="text-2xl font-semibold mb-2">Need full UK accounting?</h2>
              <p className="text-muted-foreground mb-4">
                Explore the main Neevii app for VAT filing, invoicing, and bank
                feed workflows beyond the Sheets sidebar experience.
              </p>
              <a
                href={import.meta.env.VITE_APP_URL || 'https://app.neevii.com'}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center rounded-lg border border-[#065aaf] px-6 py-3 text-sm font-semibold uppercase tracking-wide text-[#065aaf] hover:bg-[#065aaf]/10"
              >
                Visit the main Neevii app
              </a>
            </section>
          </div>
        </main>
      </div>
    </>
  );
};

export default SheetsPage;
